/**
 * TASK FIELD CONFIG — Configurazione per-tipo dei campi del Task editor.
 *
 * Permette di nascondere singoli campi dell'edit form in base al
 * TaskType, senza ramificare la logica del body con `if (type === ...)`.
 * Il body (TaskEditFormBody) legge la config come dato puro: per ogni
 * campo, salta il rendering se `hidden === true`.
 *
 * Aggiungere una regola futura = una entry in FIELD_CONFIG_BY_TYPE.
 * Aggiungere un nuovo flag (es. readOnly) = estendere TaskFieldConfig +
 * onorarlo nel body.
 */
import type { TaskType } from './taskTypes';

/** Nomi dei campi configurabili nell'edit form. Allineato 1:1 ai
 *  rendering site dentro TaskEditFormBody. */
export type TaskFieldName =
  | 'title'
  | 'type'
  | 'priority'
  | 'expirationDate'
  | 'remindDate'
  | 'recurrence'
  | 'queueId'
  | 'assignedUserId'
  | 'associatedRecords'
  | 'notes'
  | 'status';

export interface TaskFieldConfig {
  /** Se true, il campo non viene renderizzato nel form. */
  hidden?: boolean;
}

export type TaskFieldConfigMap = Partial<Record<TaskFieldName, TaskFieldConfig>>;

/**
 * Mappa per-tipo: chiave TaskType, valore config dei campi specifico.
 *
 * STATO: scaffolding intenzionale. Oggi la mappa è vuota → tutti i campi
 * sono sempre visibili in `TaskEditFormBody`, e la differenziazione
 * embedded vs standalone è gestita esclusivamente via `fieldConfigOverride`
 * passato dal call site (es. ActivityTimeline embed nasconde
 * `associatedRecords`).
 *
 * TODO popolare quando emergono regole per-tipo concrete. Esempi
 * verosimili (NON attivi oggi):
 *   note: { queueId: { hidden: true }, priority: { hidden: true } }
 *   email: { recurrence: { hidden: true } }
 *   meeting: { remindDate: { hidden: true } }
 *
 * Decisione consapevole vs drop-only: tenere il modulo permette di
 * aggiungere una regola in 1 punto (qui) invece di doverla re-introdurre
 * con un mini-refactor di `TaskEditFormBody`. Costo attuale: ~3 LOC di
 * scaffold + un dispatch `getTaskFieldConfig(...) -> {}` per render.
 */
const FIELD_CONFIG_BY_TYPE: Partial<Record<TaskType, TaskFieldConfigMap>> = {};

export function getTaskFieldConfig(type: TaskType): TaskFieldConfigMap {
  return FIELD_CONFIG_BY_TYPE[type] ?? {};
}
